import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { getResponseData, type ApiClient } from "@/modules/api/apiClient.ts";
import { getPaymentTarget, PaymentResult, type PaymentParams } from "@/modules/billing/payment/PaymentParams.ts";
import { creditBuyRequest } from "@/modules/billing/payment/paymentUtils.ts";
import { cardDefaults } from "@/modules/billing/paymentMethodDefaults.tsx";
import type { CardMethod } from "@/modules/billing/paymentMethods.ts";
import {
    t_LoadingPaymentProvider,
    t_SavingInformation,
    t_StartingPayment,
    t_WaitingForPaymentConfirmation,
} from "@/modules/billing/progressLabels.tsx";
import { throwStripeError } from "@/modules/billing/stripe/throwStripeError.ts";
import { getStripe } from "@/modules/billing/stripe/utils.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import type { Stripe } from "@stripe/stripe-js";
import type { GenericCreditBuyBody, StripeCardConfig } from "@warrenio/api-spec/spec.oats.gen";
import React, { useImperativeHandle } from "react";
import invariant from "tiny-invariant";

export function makeStripe(config: StripeCardConfig): CardMethod {
    const AddForm = React.lazy(() => import("@/modules/billing/stripe/StripeAddForm.tsx"));
    return {
        ...cardDefaults(config),
        AddForm(props) {
            return <AddForm {...props} stripe={getStripe(config)} config={config} />;
        },
        PaymentForm({ actionsRef }) {
            const apiClient = useApiClient();

            useImperativeHandle(actionsRef, () => ({
                async makePayment(params) {
                    return await cardPayment(params, apiClient, getStripe(config));
                },
            }));

            return null;
        },
    };
}

async function cardPayment(params: PaymentParams, apiClient: ApiClient, stripePromise: Promise<Stripe>) {
    const { account, getCard, progress } = params;
    const payment_object_id = getCard().id;
    const billing_account_id = account.id;

    progress(t_StartingPayment);
    const buyResult = await creditBuyRequest<GenericCreditBuyBody>(apiClient, {
        billing_account_id,
        payment_object_id,
        ...getPaymentTarget(params),
        on_session: true,
    });
    invariant("client_secret" in buyResult.data, "Must be Stripe type buy response");
    // TODO: Do we need to check `success` and `message` here?

    progress(t_LoadingPaymentProvider);
    const stripe = await stripePromise;

    progress(t_WaitingForPaymentConfirmation);
    const confirmResult = await stripe.confirmCardPayment(buyResult.data.client_secret, {
        payment_method: buyResult.data.payment_method,
    });
    throwStripeError(confirmResult.error);

    progress(t_SavingInformation);
    const _posthookResult = getResponseData(
        await apiClient.POST("/payment/credit/buy/posthook", {
            ...jsonEncodedBody,
            body: {
                billing_account_id,
                payment_object_id,
                charge_id: confirmResult.paymentIntent.id,
            },
        }),
    );

    return PaymentResult.SUCCESS;
}
